import { useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";
import {
  Iso900114001Input,
} from "../../../standards/iso-9001-14001/components/shared/Iso900114001Fields";
import { Iso900114001Modal } from "../../../standards/iso-9001-14001/components/shared/Iso900114001Modal";
import {
  Iso2700127701Button,
  Iso2700127701InputField,
  Iso2700127701Modal,
} from "../../../standards/iso-27001-27701/components/shared";
import type {
  IndicadorDataPayload,
  IndicadorDataRow,
  IndicadorField,
  IndicadoresStandard,
  IndicadorTableConfig,
} from "../types/indicadores";

type Props = {
  standard: IndicadoresStandard;
  open: boolean;
  row: IndicadorDataRow | null;
  config: IndicadorTableConfig | null;
  newTitle: string;
  editTitle: string;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (payload: IndicadorDataPayload) => void;
};

export function IndicadorDataFormModal({
  standard,
  open,
  row,
  config,
  newTitle,
  editTitle,
  submitting,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open || !config) return;

    const next: Record<string, string> = {};
    config.fields.forEach((field) => {
      const value = row?.[field.name];
      next[field.name] = value === null || value === undefined ? "" : String(value);
    });
    setValues(next);
  }, [config, open, row]);

  const annualCalculated = useMemo(() => {
    if (!config || config.kind === "electric") return false;
    if (config.annual_sum_all) return true;

    const indicator = values.indicador?.trim().toLocaleLowerCase("es-ES") ?? "";
    return config.annual_sum_indicators.some(
      (candidate) => candidate.trim().toLocaleLowerCase("es-ES") === indicator,
    );
  }, [config, values.indicador]);

  const calculatedAnnualValue = useMemo(() => {
    if (!config || !annualCalculated) return "";

    let hasValue = false;
    let total = 0;

    config.months.forEach((month) => {
      const raw = values[month]?.trim();
      if (!raw) return;

      const parsed = Number(raw.replace(",", "."));
      if (!Number.isFinite(parsed)) return;

      hasValue = true;
      total += parsed;
    });

    return hasValue ? String(total) : "";
  }, [annualCalculated, config, values]);

  if (!config) return null;

  const submit = () => {
    const payload: IndicadorDataPayload = {};

    config.fields.forEach((field) => {
      const value = values[field.name]?.trim() ?? "";

      if (value === "") {
        payload[field.name] = null;
      } else if (field.type === "number") {
        payload[field.name] = Number(value.replace(",", "."));
      } else {
        payload[field.name] = value;
      }
    });

    onSubmit(payload);
  };

  const title = row ? editTitle : newTitle;
  const fieldValue = (field: IndicadorField) => (
    field.name === "anual" && annualCalculated
      ? calculatedAnnualValue
      : values[field.name] ?? ""
  );
  const updateField = (field: IndicadorField, value: string) => {
    setValues((current) => ({ ...current, [field.name]: value }));
  };

  if (standard === "iso-9001-14001") {
    return (
      <Iso900114001Modal
        open={open}
        title={title}
        eyebrow={row ? "Edición" : "Nuevo registro"}
        submitting={submitting}
        error={error}
        onClose={onClose}
        onSubmit={submit}
        wide
        footer={
          <>
            <button
              type="button"
              className="calidad-back-button"
              onClick={onClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="calidad-primary-button"
              disabled={submitting || !values.anio?.trim()}
            >
              <Save size={16} />
              {submitting ? "Guardando..." : "Guardar"}
            </button>
          </>
        }
      >
        {config.fields.map((field) => (
          <Iso900114001Input
            key={field.name}
            label={field.label}
            type={field.type === "number" ? "number" : "text"}
            step={field.name === "anio" ? "1" : "any"}
            value={fieldValue(field)}
            required={field.required}
            readOnly={field.name === "anual" && annualCalculated}
            onChange={(event) => updateField(field, event.target.value)}
          />
        ))}
        {annualCalculated ? (
          <p className="indicadores-form-note">
            El valor anual se calculará automáticamente como suma de los meses.
          </p>
        ) : null}
      </Iso900114001Modal>
    );
  }

  return (
    <Iso2700127701Modal
      open={open}
      title={title}
      description="Completa los campos definidos para este indicador."
      size="wide"
      onClose={onClose}
      onSubmit={submit}
      footer={
        <>
          <Iso2700127701Button
            variant="secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancelar
          </Iso2700127701Button>
          <Iso2700127701Button
            variant="primary"
            type="submit"
            icon={<Save size={16} />}
            disabled={submitting || !values.anio?.trim()}
          >
            {submitting ? "Guardando..." : "Guardar"}
          </Iso2700127701Button>
        </>
      }
    >
      <div className="iso27001-form-grid">
        {config.fields.map((field) => (
          <Iso2700127701InputField
            key={field.name}
            label={field.label}
            type={field.type === "number" ? "number" : "text"}
            step={field.name === "anio" ? "1" : "any"}
            value={fieldValue(field)}
            required={field.required}
            readOnly={field.name === "anual" && annualCalculated}
            onChange={(event) => updateField(field, event.target.value)}
          />
        ))}
      </div>
      {annualCalculated ? (
        <p className="indicadores-form-note">
          El valor anual se calculará automáticamente como suma de los meses.
        </p>
      ) : null}
      {error ? <div className="iso27001-form-error">{error}</div> : null}
    </Iso2700127701Modal>
  );
}
